import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  ArchiveIcon,
  BellIcon,
  BriefcaseIcon,
  CalendarIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardCheckIcon,
  ClipboardListIcon,
  CogIcon,
  CollectionIcon,
  CubeIcon,
  CubeTransparentIcon,
  CurrencyRupeeIcon,
  DocumentReportIcon,
  DocumentTextIcon,
  FolderOpenIcon,
  IdentificationIcon,
  InboxInIcon,
  LibraryIcon,
  LocationMarkerIcon,
  MapIcon,
  MenuIcon,
  OfficeBuildingIcon,
  PresentationChartLineIcon,
  ReceiptTaxIcon,
  SearchIcon,
  ShieldCheckIcon,
  ShoppingCartIcon,
  SupportIcon,
  TemplateIcon,
  TruckIcon,
  UserGroupIcon,
  UsersIcon,
  ViewGridIcon,
} from "@heroicons/react/outline";
import { useAuth } from "../../context/AuthContext";
import { useSidebar } from "../../context/SidebarContext";
import { canAccessAction } from "../../routes/routePermissions";

interface SidebarLink {
  path: string;
  label: string;
  icon: ReactNode;
  action: string;
}

interface SidebarSection {
  category: string;
  icon: ReactNode;
  links: SidebarLink[];
}

type HeroIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const sectionIcon = (Icon: HeroIcon) => <Icon aria-hidden="true" className="h-5 w-5 stroke-[2]" />;
const linkIcon = (Icon: HeroIcon) => <Icon aria-hidden="true" className="h-5 w-5 stroke-[2]" />;

const sidebarSections: SidebarSection[] = [
  {
    category: "Dashboard",
    icon: sectionIcon(ViewGridIcon),
    links: [{ path: "/admin/dashboard", label: "Dashboard", icon: linkIcon(ViewGridIcon), action: "view_dashboard" }],
  },
  {
    category: "Sales",
    icon: sectionIcon(PresentationChartLineIcon),
    links: [
      { path: "/admin/enquiries", label: "Enquiries", icon: linkIcon(InboxInIcon), action: "view_enquiries" },
      { path: "/admin/quotations", label: "Quotations", icon: linkIcon(DocumentTextIcon), action: "view_quotations" },
      { path: "/admin/clients", label: "Clients", icon: linkIcon(UserGroupIcon), action: "manage_clients" },
      { path: "/admin/sales-customers", label: "Sales Customers", icon: linkIcon(UsersIcon), action: "manage_sales_customers" },
    ],
  },
  {
    category: "Projects",
    icon: sectionIcon(OfficeBuildingIcon),
    links: [
      { path: "/admin/projects", label: "Projects", icon: linkIcon(OfficeBuildingIcon), action: "view_projects" },
      { path: "/admin/project-documents", label: "Project Documents", icon: linkIcon(FolderOpenIcon), action: "manage_project_documents" },
      { path: "/admin/subcontract-works", label: "Subcontract Works", icon: linkIcon(BriefcaseIcon), action: "manage_subcontract_works" },
      { path: "/admin/daily-tasks", label: "Daily Tasks", icon: linkIcon(ClipboardCheckIcon), action: "track_daily_tasks" },
      { path: "/admin/daily-notes", label: "Daily Notes", icon: linkIcon(ClipboardListIcon), action: "write_daily_notes" },
    ],
  },
  {
    category: "Finance",
    icon: sectionIcon(CurrencyRupeeIcon),
    links: [
      { path: "/admin/accounts", label: "Accounts", icon: linkIcon(LibraryIcon), action: "manage_accounts" },
      { path: "/admin/expenses", label: "Expenses", icon: linkIcon(ReceiptTaxIcon), action: "log_expenses" },
      { path: "/admin/security-deposits", label: "Security Deposits", icon: linkIcon(ShieldCheckIcon), action: "manage_security_deposits" },
      { path: "/admin/EMD", label: "EMD", icon: linkIcon(CurrencyRupeeIcon), action: "manage_EMD" },
      { path: "/admin/travel-allowance", label: "Travel Allowance", icon: linkIcon(LocationMarkerIcon), action: "approve_travel_allowance" },
    ],
  },
  {
    category: "Operations",
    icon: sectionIcon(CubeTransparentIcon),
    links: [
      { path: "/admin/assets", label: "Assets", icon: linkIcon(ArchiveIcon), action: "manage_assets" },
      { path: "/admin/purchases", label: "Purchases", icon: linkIcon(ShoppingCartIcon), action: "view_purchase_requests" },
      { path: "/admin/stock-management", label: "Stock Management", icon: linkIcon(CubeTransparentIcon), action: "manage_stock" },
      { path: "/admin/labour-management", label: "Labour Management", icon: linkIcon(UserGroupIcon), action: "manage_labour" },
      { path: "/admin/vehicle-management", label: "Vehicle Management", icon: linkIcon(TruckIcon), action: "manage_vehicle" },
      { path: "/admin/tools-management", label: "Tools Management", icon: linkIcon(CogIcon), action: "manage_tools" },
      { path: "/admin/hr", label: "HR", icon: linkIcon(IdentificationIcon), action: "view_hr" },
      { path: "/admin/employees", label: "Employees", icon: linkIcon(UserGroupIcon), action: "manage_employees" },
      { path: "/admin/users", label: "Users", icon: linkIcon(UsersIcon), action: "manage_users" },
    ],
  },
  {
    category: "Masters",
    icon: sectionIcon(CollectionIcon),
    links: [
      { path: "/admin/services", label: "Services", icon: linkIcon(SupportIcon), action: "define_services" },
      { path: "/admin/suppliers", label: "Suppliers", icon: linkIcon(TruckIcon), action: "manage_suppliers" },
      { path: "/admin/materials", label: "Materials", icon: linkIcon(CubeIcon), action: "manage_materials" },
      { path: "/admin/bank-details", label: "Bank Details", icon: linkIcon(LibraryIcon), action: "manage_bank_accounts" },
      { path: "/admin/work-types", label: "Work Types", icon: linkIcon(CogIcon), action: "define_work_types" },
      { path: "/admin/work-categories", label: "Work Categories", icon: linkIcon(CollectionIcon), action: "define_work_categories" },
      { path: "/admin/project-sections", label: "Project Sections", icon: linkIcon(TemplateIcon), action: "define_project_sections" },
      { path: "/admin/work-locations", label: "Work Locations", icon: linkIcon(MapIcon), action: "define_work_locations" },
      { path: "/admin/tax-types", label: "Tax Types", icon: linkIcon(ReceiptTaxIcon), action: "define_tax_types" },
      { path: "/admin/holidays", label: "Holidays", icon: linkIcon(CalendarIcon), action: "define_holidays" },
    ],
  },
  {
    category: "Reports",
    icon: sectionIcon(ChartBarIcon),
    links: [
      { path: "/admin/reports", label: "Reports", icon: linkIcon(ChartBarIcon), action: "view_reports" },
      { path: "/admin/attendance-report", label: "Attendance Report", icon: linkIcon(ClipboardCheckIcon), action: "view_attendance_reports" },
      { path: "/admin/activity-logs", label: "Activity Logs", icon: linkIcon(DocumentReportIcon), action: "view_activity_logs" },
    ],
  },
  {
    category: "Others",
    icon: sectionIcon(BellIcon),
    links: [
      { path: "/admin/subscription", label: "Plan & Billing", icon: linkIcon(CurrencyRupeeIcon), action: "view_dashboard" },
      { path: "/admin/reminders", label: "Reminders", icon: linkIcon(BellIcon), action: "set_reminders" },
      { path: "/admin/settings", label: "Settings", icon: linkIcon(CogIcon), action: "configure_settings" },
    ],
  },
  {
    category: "SaaS Platform",
    icon: sectionIcon(ShieldCheckIcon),
    links: [
      { path: "/super-admin", label: "Platform Overview", icon: linkIcon(ChartBarIcon), action: "manage_platform" },
      { path: "/super-admin/tenants", label: "Tenants Directory", icon: linkIcon(OfficeBuildingIcon), action: "manage_all_companies" },
      { path: "/super-admin/plans", label: "Plan Matrix", icon: linkIcon(CollectionIcon), action: "manage_plans" },
    ],
  },
];

const isPathActive = (currentPath: string, linkPath: string) => {
  if (linkPath === "/super-admin") {
    return currentPath === "/super-admin" || currentPath === "/super-admin/";
  }
  if (linkPath === "/admin/dashboard") {
    return currentPath === "/admin/dashboard";
  }
  return currentPath === linkPath || currentPath.startsWith(`${linkPath}/`);
};

const getActiveSection = (pathname: string, sections: SidebarSection[] = sidebarSections) =>
  sections.find((section) =>
    section.links.some((link) => isPathActive(pathname, link.path))
  )?.category || "Dashboard";

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions, roles, isPlatformAdmin } = useAuth();
  const { isCollapsed, toggleSidebar, closeSidebar } = useSidebar();
  const [searchTerm, setSearchTerm] = useState("");
  const isSuperAdmin = useMemo(() => {
    const isImpersonating = typeof window !== "undefined" && window.sessionStorage.getItem("is_impersonating") === "true";
    return isPlatformAdmin && !isImpersonating;
  }, [isPlatformAdmin]);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() =>
    sidebarSections.reduce<Record<string, boolean>>((acc, section) => {
      acc[section.category] = ["Dashboard", "Sales", "Projects", "SaaS Platform"].includes(section.category);
      return acc;
    }, {})
  );

  const accessibleSections = useMemo(
    () => {
      if (isSuperAdmin) {
        return sidebarSections.filter((section) => section.category === "SaaS Platform");
      }
      return sidebarSections
        .filter((section) => section.category !== "SaaS Platform")
        .map((section) => ({
          ...section,
          links: section.links.filter((link) => canAccessAction(link.action, permissions, roles, isPlatformAdmin)),
        }))
        .filter((section) => section.links.length > 0);
    },
    [isSuperAdmin, permissions, roles]
  );

  useEffect(() => {
    const activeCategory = getActiveSection(location.pathname, accessibleSections);
    setOpenSections((current) => ({ ...current, [activeCategory]: true }));
  }, [accessibleSections, location.pathname]);

  const filteredSections = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      return accessibleSections;
    }

    return accessibleSections
      .map((section) => ({
        ...section,
        links: section.links.filter((link) => link.label.toLowerCase().includes(term)),
      }))
      .filter((section) => section.links.length > 0);
  }, [accessibleSections, searchTerm]);

  const handleCollapsedNavigate = (section: SidebarSection) => {
    const activeLink = section.links.find((link) => isPathActive(location.pathname, link.path));
    navigate(activeLink?.path || section.links[0].path);
  };

  const handleLinkClick = () => {
    if (window.innerWidth < 1024) {
      closeSidebar();
    }
  };

  return (
    <>
      {!isCollapsed && (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-slate-900/25 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex border-r border-slate-200/80 bg-white text-slate-700 shadow-sm transition-all duration-200 ${
          isCollapsed ? "w-20 -translate-x-full lg:translate-x-0" : "w-[260px] translate-x-0"
        }`}
      >
        {isCollapsed ? (
          <div className="hidden h-full w-20 flex-col items-center justify-between px-2 py-5 lg:flex">
            <div className="flex flex-col items-center gap-4 w-full">
              {/* Brand Icon */}
              <button
                type="button"
                onClick={() => navigate(isSuperAdmin ? "/super-admin" : "/admin/dashboard")}
                className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-900 text-white shadow-md shadow-emerald-900/20"
                title="Strux CMS"
              >
                <div className="h-5 w-5 rounded-md bg-emerald-400 flex items-center justify-center font-black text-emerald-950 text-xs">
                  S
                </div>
              </button>

              <button
                type="button"
                onClick={toggleSidebar}
                className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Expand navigation"
                title="Expand navigation"
              >
                <MenuIcon aria-hidden="true" className="h-4 w-4" />
              </button>

              <div className="w-full space-y-2 mt-2">
                {accessibleSections.map((section) => {
                  const active = section.links.some((link) => isPathActive(location.pathname, link.path));
                  return (
                    <button
                      key={section.category}
                      type="button"
                      onClick={() => handleCollapsedNavigate(section)}
                      className={`group relative flex h-10 w-full items-center justify-center rounded-xl transition ${
                        active
                          ? "bg-zinc-900 text-white shadow-sm"
                          : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                      }`}
                      title={section.category}
                    >
                      {section.icon}
                      <span className="pointer-events-none absolute left-full ml-2 rounded-lg bg-zinc-900 px-2.5 py-1 text-xs font-medium text-white opacity-0 shadow-md transition group-hover:opacity-100 z-50">
                        {section.category}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full w-full flex-col justify-between">
            <div className="flex flex-col min-h-0 flex-1">
              {/* Brand Header */}
              <div className="flex items-center justify-between px-5 pt-5 pb-3">
                <button
                  type="button"
                  onClick={() => navigate(isSuperAdmin ? "/super-admin" : "/admin/dashboard")}
                  className="flex items-center gap-3 text-left group"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#064e3b] text-white shadow-md shadow-emerald-950/20">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#34d399" />
                      <path d="M2 17L12 22L22 17" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M2 12L12 17L22 12" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-extrabold text-base tracking-tight text-slate-900 block leading-tight">
                      Strux<span className="text-emerald-700">CMS</span>
                    </span>
                    <span className="text-[11px] font-semibold text-slate-400 block tracking-wide">
                      {isSuperAdmin ? "Platform Owner" : "Enterprise Edition"}
                    </span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={toggleSidebar}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Collapse navigation"
                  title="Collapse navigation"
                >
                  <ChevronLeftIcon aria-hidden="true" className="h-4 w-4 stroke-[2.2]" />
                </button>
              </div>

              {/* Quick Search */}
              <div className="px-4 py-2">
                <div className="relative">
                  <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search"
                    className="h-10 w-full rounded-xl border border-slate-200/80 bg-slate-50/70 pl-9 pr-9 text-xs font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-white focus:ring-2 focus:ring-emerald-600/10"
                  />
                </div>
              </div>

              {/* Nav Sections */}
              <nav className="min-h-0 flex-1 space-y-4 overflow-y-auto px-3 py-2">
                {filteredSections.map((section) => {
                  const isOpen = searchTerm.trim() ? true : openSections[section.category];
                  const sectionPanelId = `sidebar-section-${section.category.toLowerCase().replace(/\s+/g, "-")}`;

                  return (
                    <div key={section.category} className="space-y-1">
                      <div className="flex items-center justify-between px-3 py-1">
                        <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                          {section.category === "SaaS Platform" ? "PLATFORM GOVERNANCE" : section.category}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setOpenSections((current) => ({
                              ...current,
                              [section.category]: !current[section.category],
                            }))
                          }
                          aria-expanded={isOpen}
                          aria-controls={sectionPanelId}
                          className="text-slate-400 hover:text-slate-600 p-0.5 rounded"
                        >
                          {isOpen ? (
                            <ChevronDownIcon aria-hidden="true" className="h-3 w-3 stroke-[2.2]" />
                          ) : (
                            <ChevronRightIcon aria-hidden="true" className="h-3 w-3 stroke-[2.2]" />
                          )}
                        </button>
                      </div>

                      {isOpen && (
                        <ul id={sectionPanelId} className="space-y-1">
                          {section.links.map((link) => {
                            const active = isPathActive(location.pathname, link.path);
                            return (
                              <li key={link.path}>
                                <NavLink
                                  to={link.path}
                                  onClick={handleLinkClick}
                                  className={`flex items-center justify-between w-full px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all duration-150 ${
                                    active
                                      ? "bg-zinc-900 text-white shadow-sm"
                                      : "text-slate-600 hover:bg-slate-100/80 hover:text-slate-900"
                                  }`}
                                >
                                  <div className="flex items-center gap-3 min-w-0">
                                    <span className={`flex-shrink-0 ${active ? "text-white" : "text-slate-400"}`}>
                                      {link.icon}
                                    </span>
                                    <span className="truncate">{link.label}</span>
                                  </div>
                                </NavLink>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                })}

                {filteredSections.length === 0 && (
                  <div className="px-3 py-8 text-center text-xs text-slate-400 font-medium">No matching menus found.</div>
                )}
              </nav>
            </div>

            {/* Bottom Card (Upgrade / Status) */}
            <div className="p-3 border-t border-slate-100">
              <div className="rounded-2xl bg-gradient-to-br from-emerald-50/80 via-slate-50 to-slate-100/80 p-3.5 border border-emerald-100/60 shadow-sm space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-extrabold text-slate-900 flex items-center gap-1.5">
                    {isSuperAdmin ? "Super Admin 🛡️" : "Enterprise Pro 👑"}
                  </span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                    Active
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 leading-snug">
                  {isSuperAdmin
                    ? "Full SaaS control, dynamic pricing & tenant telemetry."
                    : "Full multi-project & commercial modules enabled."}
                </p>
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => navigate(isSuperAdmin ? "/super-admin/plans" : "/admin/subscription")}
                    className="w-full text-center py-1.5 px-3 rounded-xl bg-[#064e3b] hover:bg-[#047857] text-white text-xs font-bold transition-all shadow-sm flex items-center justify-center gap-1.5"
                  >
                    {isSuperAdmin ? "Manage Plans" : "Manage Plan"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
